import { Injectable, inject } from '@angular/core';
import {
    NavigationCancel,
    NavigationCancellationCode,
    NavigationEnd,
    NavigationError,
    NavigationSkipped,
    Router,
} from '@angular/router';
import { FuseSplashScreenService } from '@fuse/services/splash-screen';
import { filter, first, race, timer } from 'rxjs';

/** Longest we'll wait for a router event before forcing the splash to hide. */
export const SAFETY_TIMEOUT_MS = 10_000;

/**
 * #1398: `FuseSplashScreenService` (vendored, do-not-edit) hides the boot
 * splash on the first `NavigationEnd` only. On an intermittent cold load the
 * first navigation can resolve as `NavigationCancel` (a guard redirect) or
 * `NavigationError` instead — a case its `take(1)` on `NavigationEnd` never
 * sees, leaving `body` without `fuse-splash-screen-hidden` and the splash
 * opaque over a fully rendered page indefinitely. Reproduced on DEV: ~2/15
 * cold loads of /my-notifications, /me, /overview got stuck this way.
 *
 * Widens the net without touching the vendored service — a second,
 * independent `hide()` call is a no-op, since `hide()` only adds a CSS
 * class. Deliberately NOT keyed to `ApplicationRef.isStable`: this app's
 * esbuild builder uses native `import()` for lazy routes, which isn't
 * reliably tracked as a pending zone macrotask, so "stable" can fire before
 * the target route's chunk has actually loaded — hiding the splash over a
 * still-empty outlet. `race()` against a hard timeout covers the same
 * "nothing we expected ever fired" case without that risk, and is
 * self-cancelling (no leftover timer once a router event wins).
 *
 * `NavigationCancel` is NOT always terminal: Angular fires it with
 * `code: Redirect`/`SupersededByNewNavigation` for an ordinary in-flight
 * guard redirect (e.g. `RoleRedirectGuard` on `/`, or `AuthGuard` bouncing
 * to `/sign-in`) — the ROUTE EVERY LOGIN GOES THROUGH — while a follow-up
 * navigation to the real destination is still loading its lazy chunk.
 * Treating that as "settled" hid the splash before the destination
 * rendered, on every normal login. Filtered out here, mirroring Angular's
 * own `afterNextNavigation()` definition of a completed navigation
 * (`NavigationEnd`/`NavigationSkipped`, or `NavigationCancel`/`Error` that
 * isn't a redirect-in-progress).
 */
@Injectable({ providedIn: 'root' })
export class SplashScreenGuardService {
    private readonly _router = inject(Router);
    private readonly _splashScreen = inject(FuseSplashScreenService);

    constructor() {
        const routerSettled$ = this._router.events.pipe(
            filter((event) => {
                if (
                    event instanceof NavigationEnd ||
                    event instanceof NavigationSkipped ||
                    event instanceof NavigationError
                ) {
                    return true;
                }
                if (event instanceof NavigationCancel) {
                    return (
                        event.code !== NavigationCancellationCode.Redirect &&
                        event.code !==
                            NavigationCancellationCode.SupersededByNewNavigation
                    );
                }
                return false;
            })
        );

        race(routerSettled$, timer(SAFETY_TIMEOUT_MS))
            .pipe(first())
            .subscribe(() => this._splashScreen.hide());
    }
}
